/**
 * Overview's calendar choices as they sit in the address: the view, the month
 * or week on screen, the sub-regions the legend has hidden, and whether
 * cancelled workshops show. A value at its default is left out, so a plain
 * Overview link stays plain.
 */
import type { SearchParamCodec } from '../../../../hooks/use-search-param-state';
import type { CalendarChoices } from '../../primitives/use-calendar-state';
import {
  DEFAULT_WEEK_STARTS_ON,
  startOfWeekUtc,
  utcDayKey,
  viewerDayUtc,
} from '../../primitives/workshops-calendar-dates';
import type { CalendarView } from '../../primitives/workshopsCalendarTypes';

const VIEW_PARAM = 'view';
const DATE_PARAM = 'date';
const HIDE_PARAM = 'hide';
const CANCELLED_PARAM = 'cancelled';

const VIEW_PARAM_VALUES: Record<CalendarView, string> = {
  Month: 'month',
  Week: 'week',
  List: 'list',
};

const defaultView = (views: CalendarView[]): CalendarView =>
  views.includes('Month') ? 'Month' : views[0];

/** The first day of the anchor's week in Week view, or of its month otherwise. */
function periodStart(view: CalendarView, anchor: Date): string {
  return utcDayKey(
    view === 'Week'
      ? startOfWeekUtc(anchor, DEFAULT_WEEK_STARTS_ON)
      : new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1)),
  );
}

function parseDay(value: string | null): Date | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const day = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(day.getTime()) && utcDayKey(day) === value ? day : null;
}

/** Reads the choices back, falling back to the default for any value that is missing or bad. */
export function readCalendarParams(
  params: URLSearchParams,
  views: CalendarView[],
  today: Date,
): CalendarChoices {
  const viewValue = params.get(VIEW_PARAM);
  return {
    view: views.find((v) => VIEW_PARAM_VALUES[v] === viewValue) ?? defaultView(views),
    anchor: parseDay(params.get(DATE_PARAM)) ?? today,
    hidden: params.getAll(HIDE_PARAM),
    showCancelled: params.get(CANCELLED_PARAM) === '1',
  };
}

/** The address with these choices in it, leaving every other entry alone. */
export function writeCalendarParams(
  params: URLSearchParams,
  choices: CalendarChoices,
  views: CalendarView[],
  today: Date,
): URLSearchParams {
  const next = new URLSearchParams(params);
  [VIEW_PARAM, DATE_PARAM, HIDE_PARAM, CANCELLED_PARAM].forEach((key) => next.delete(key));

  if (choices.view !== defaultView(views)) next.set(VIEW_PARAM, VIEW_PARAM_VALUES[choices.view]);
  const start = periodStart(choices.view, choices.anchor);
  if (start !== periodStart(choices.view, today)) next.set(DATE_PARAM, start);
  choices.hidden.forEach((label) => next.append(HIDE_PARAM, label));
  if (choices.showCancelled) next.set(CANCELLED_PARAM, '1');
  return next;
}

const ALL_VIEWS: CalendarView[] = ['Month', 'Week', 'List'];
const PHONE_VIEWS: CalendarView[] = ['Month', 'Week'];

/** The calendar views on offer: a phone leaves out List, since the List / Calendar
 *  switch above its calendar already offers one. */
export function overviewCalendarViews(isPhone: boolean): CalendarView[] {
  return isPhone ? PHONE_VIEWS : ALL_VIEWS;
}

/** The calendar choices as an address value, read against the viewer's today. */
export function calendarParams(views: CalendarView[]): SearchParamCodec<CalendarChoices> {
  return {
    read: (params) => readCalendarParams(params, views, viewerDayUtc()),
    write: (params, choices) => writeCalendarParams(params, choices, views, viewerDayUtc()),
  };
}
